import clsx from 'clsx';
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import styles from './AvailabilityTab.module.css';
import {
  AggregateStatusIndicator,
  VcStatusIndicator,
  PartAvailabilityIndicator,
  Table,
} from '..';
import {
  loadAsnDates,
  splitDescription,
  compareByDateAndSerialNumber,
  formatDataToIncludeColour,
  getAggregateStatusAndModifiedAtAndCommentForAvailabilityScreen,
} from '../../utils/tableUtils';
import { optionalColumnsWithShift } from '../../staticData';
import { LinearProgress } from '@material-ui/core';

import {
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  ASN_STATUS,
  MESSAGE_TYPE,
  DISPLAY_MESSAGES,
  RESOURCE_TYPE,
  USER_OPERATIONS,
  DATE_FORMAT,
  AGGREGATES,
  VC_ASN_STATUS,
} from '../../constants';
import { statusStringMap } from '../../staticData';
import { API } from '../../apis/api';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import AggregateToolbar from '../AggregateToolbar/AggregateToolbar';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { withAllowedOperationsProvider } from '../../hocs';
import { buildErrorMessage } from '../../apis/calls';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { InfoIcon } from '../../icons';
import { downloadCsv } from '../../utils/utils';

const AvailabilityTab = ({
  aggregate: materialGroupCluster,
  hasActiveEdits,
  setHasActiveEdits,
}) => {
  const ROW_HEIGHT = 50;

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const {showPopup, showInternalError, showLoading, closePopUp} = usePopupManager();
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);
  const [asnDates, setAsnDates] = useState([]);
  const [activeAsnDate, setActiveAsnDate] = useState(null);
  const isLucknowProlifePlant = plant.label === 'Lucknow-Prolife'
  const parentName = isLucknowProlifePlant ? 'Parent' : 'VC'

  const markAvailable = async rows => {
    try {
      showLoading();
      const payload = rows.map(({ asnId,materialNumber }) => ({
        asnId,
        aggregateMaterialNumber:materialNumber,
        status: AGGREGATE_STATUS.READY_FOR_PICKUP,
        availability: true,
      }));

      await API.patch(
        API_RESOURCE_URLS.asnMaterialGroupClusters(
          plant.value,
          line.value,
          materialGroupCluster,
        ),
        payload,
      );
      loadAggregateAsnForDate(materialGroupCluster, activeAsnDate); //reload data
      closePopUp();
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: DISPLAY_MESSAGES.AGGREGATE_STATUS_UPDATED,
        info: '',
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        error: err,
        contextText: isLucknowProlifePlant ? DISPLAY_MESSAGES.PART_UPDATE_FAILED :DISPLAY_MESSAGES.VC_UPDATE_FAILED,
      });
    }
  };

  

  const aggregateColumns = [
    
    {
      width: 150,
      title: 'Part Availability',
      field: 'materialNumber',
      alignment:
        materialGroupCluster !== AGGREGATES.MATERIAL ? 'left' : 'center',
      style: {
        boxSizing: 'content-box',
      },
      enableSearch: true,
      enableFilter: true,
      render: row => (
        <>
          {(
            <div
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                minWidth: '97px',
              }}
              status={row.materialStatus}
            >
              {row.materialNumber}
            </div>
          )}
          <PartAvailabilityIndicator
            style={{ marginLeft: '6px', height: '17px', width: '17px' }}
            availability={row.materialAvailability}
            partNumber={row.materialNumber}
          />
        </>
      ),
    },
     {
          width: 210,
          title: 'Part Description',
          field: 'materialDescription',
          enableSearch: true,
          enableFilter: true,
        },
  ].filter(c => c);

  const vcColumns = [
    {
      width: 120,
      title: 'ASN',
      field: 'asn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: `${parentName} Number`,
      field: 'vc',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 180,
      title: `${parentName} Description`,
      field: 'vcDescription',
      render: (
        row,
        _,
        { openEllipsisPopupForEventWithText, closeEllipsisPopup, onCopy },
      ) => {
        const [descriptionHead, descriptionTail] = splitDescription(
          row.vcDescription,
        );
        return (
          <div
            className={row.vcStatus === VC_ASN_STATUS.LINE_OUT && styles.redRow}
            onMouseEnter={e =>
              openEllipsisPopupForEventWithText(e, row.vcDescription)
            }
            onMouseLeave={closeEllipsisPopup}
            onDoubleClick={() => onCopy(row.vcDescription)}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <span className={styles.boldBlue}>{descriptionHead}</span>{' '}
            {descriptionTail}
          </div>
        );
      },
      searchFn: (fieldValue, searchValue) =>
        fieldValue.toLowerCase().includes(searchValue.toLowerCase()),
    },
    {
      width: 120,
      title: `${parentName} Status`,
      field: 'status',
      customFilterOptions: [
        {
          value: 'red',
          node: <InfoIcon style={{ color: '#F17C7C', fontSize: '1em' }} />,
        },
        {
          value: 'green',
          node: <InfoIcon style={{ color: '#49a47e', fontSize: '1em' }} />,
        },
        {
          value: 'grey',
          node: <InfoIcon style={{ color: '#7070708c', fontSize: '1em' }} />,
        },
      ],
      filterFn: (row, option) => {
        if (option === 'red')
          return (
            row.vcStatus === VC_ASN_STATUS.LINE_OUT ||
            row.hasUnAvailableAggregates
          );
        if (option === 'green')
          return (
            row.vcStatus !== VC_ASN_STATUS.LINE_OUT &&
            row.materialGroupClusters
              .map(group => ({
                name: group.name,
                ...group.materials.map(({ asnMaterial }) => row.materialNumber === asnMaterial.material),
              }))
              .every(material => material.availability)
          );
        if (option === 'grey')
          return (
            row.vcStatus !== VC_ASN_STATUS.LINE_OUT &&
            !row.hasUnAvailableAggregates &&
            row.materialGroupClusters
              .map(group => ({
                name: group.name,
                ...group.materials.map(({ asnMaterial }) => row.materialNumber === asnMaterial.material),
              }))
              .some(material => !material.availability)
          );
      },
      cellStyles: { height: '84%', marginLeft: '1em' },
      render: row => {
        if (row.vcStatus === VC_ASN_STATUS.LINE_OUT)
          return <VcStatusIndicator rowData={row} fontSize="17px" />;

        return (
          <AggregateStatusIndicator
            rowData={row}
            currentAggregate={materialGroupCluster}
            fontSize="17px"
          />
        );
      },
    },
  ].map(c => ({ ...c, enableSearch: true, enableFilter: true }));

  const actionColumns = [
    {
      width: 170,
      title:
        materialGroupCluster === AGGREGATES.MATERIAL
          ? 'Material Status'
          : 'Aggregate Status',
      field: 'materialStatus',
      enableSearch: true,
      enableFilter: true,
      render: row => {
        if (row.materialStatus === statusStringMap[AGGREGATE_STATUS.PLANNED])
          return <span style={{ opacity: 0.5 }}>{row.materialStatus}</span>;
        return row.materialStatus;
      },
    },
    {
      width: 90,
      title: 'Updated At',
      field: '_materialUpdatedAt',
      cellStyles: { padding: '2px 12px' },
      render: ({ materialUpdatedAt }) => (
        <div style={{ textAlign: 'center' }}>
          <div className={styles.time}>
            {materialUpdatedAt && moment(materialUpdatedAt).format('hh:mm A')}
          </div>
          <div className={styles.date}>
            {materialUpdatedAt &&
              moment(materialUpdatedAt).format('DD-MM-YYYY')}
          </div>
        </div>
      ),
    },
  ].filter(c => c);

  const materialWisePartList = (materialGroupName,materialList) =>{
    if(materialList !== undefined)
    {
      if(materialGroupName===AGGREGATES.MATERIAL)
        return [materialList[0]];
      else
        return materialList;
    }
  }
  const processVcAsnResponseForMaterialGroup = (asnResponse, materialGroupName) => {
    const processedResponse = asnResponse.data
      .map(({ status, ...asnData }) => {
        const updatedMaterialGroupClusters = asnData.materialGroupClusters.map(
          ({ name, materials }) => ({
            name,
            materials: materials.map(material =>
              getAggregateStatusAndModifiedAtAndCommentForAvailabilityScreen(material),
            ),
          }),
        );
        
        const materialList = updatedMaterialGroupClusters.filter( 
        ({ name }) => name === materialGroupName,
          )[0]?.materials;
        
        return materialWisePartList(materialGroupName,materialList)?.map(part => ( {
          ...asnData,
          id: asnData.asnId +part.material,
          vcStatus: status,
          materialGroupClusters: [...updatedMaterialGroupClusters],
          materialNumber:
             part.material,
          materialStatus: part.aggregateStatus,
          materialDescription:
             part.materialDescription,
          materialAvailability:
          part.aggregateStatus === AGGREGATE_STATUS.READY_FOR_PICKUP,
          materialUpdatedAt: part.aggregateModifiedAt,
        }));
      })
      .filter(i => i)
      .filter(
        ({ materialStatus }) => materialStatus !== AGGREGATE_STATUS.LINE_OUT,
      )
     
     const asnListData=[];
     const finalData = processedResponse?.map(asns => asns.map(asnList =>asnListData.push(asnList)));
  
     const multiPartProcess =   asnListData?.map(asn => {
        const hasUnAvailableAggregates = asn.materialGroupClusters
          .filter(({ name,materials }) => name === materialGroupName && asn.materialNumber === materials.material)
          .some(
            ({ aggregateStatus }) =>
              aggregateStatus === AGGREGATE_STATUS.LINE_OUT,
          );
        return { ...asn, hasUnAvailableAggregates };
      }).sort((a, b) =>
      compareByDateAndSerialNumber(a, b, 'date', 'serialNumber'),
    );
    return multiPartProcess;
  }

  const fetchAggregatesRowsDataForDate = async (plantId, lineId, dateString) =>
    API.get(API_RESOURCE_URLS.getoutsourceparts(plantId, lineId), {
      params: {
        from: dateString,
        to: dateString,
        status: [AGGREGATE_STATUS.DISPATCHED,AGGREGATE_STATUS.BOOKED,AGGREGATE_STATUS.ASN_GENERATED,AGGREGATE_STATUS.READY_FOR_DROP],
      },
    });

    const fetchPreviousAggregatesRowsData = async (plantId, lineId, dateString) =>
        API.get(API_RESOURCE_URLS.getoutsourceparts(plantId, lineId), {
          params: {
            before: dateString,
            status: [AGGREGATE_STATUS.BOOKED],
          },
        });

  const fetchAndBuildAggregatesRowsDataForDate = async (
    materialGroupName,
    date,
  ) => {
    const dateString = date.format(DATE_FORMAT.ISO);
    const asnResponse = await fetchAggregatesRowsDataForDate(plant.value, line.value, dateString);
    const processedResponse = processVcAsnResponseForMaterialGroup(
      asnResponse,
      materialGroupName,
    );
    const data = formatDataToIncludeColour(processedResponse);
    return data;
  };

  const fetchAndBuildPreviousPendingAggregatesRowsData = async materialGroupName => {
    const todaysDateString = moment().format(DATE_FORMAT.ISO);
    const asnResponse = await fetchPreviousAggregatesRowsData(plant.value, line.value, todaysDateString);

    const processedResponse = processVcAsnResponseForMaterialGroup(
      asnResponse,
      materialGroupName,
    );
    const data = formatDataToIncludeColour(processedResponse);
    return data;
  };

  const getAggregatesDataBasedOnDate = async (aggregate, date) => {
    const today = moment();
    if (date.isBefore(today, 'day'))
      return fetchAndBuildPreviousPendingAggregatesRowsData(aggregate);
    return fetchAndBuildAggregatesRowsDataForDate(aggregate, date);
  };

  const loadAggregateAsnForDate = async (aggregate, date) => {
    if (!date)
      return setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
    try {
      setRowsData(prevState => ({ ...prevState, loading: true }));
      const data = await getAggregatesDataBasedOnDate(aggregate, date);

      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return setRowsData(prevState => ({ ...prevState, loading: false }));

      data.forEach(
        row => (row.materialStatus = statusStringMap[row.materialStatus]),
      );

      setRowsData({
        loading: false,
        data,
        filteredData: [...data],
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      setRowsData(prevState => ({ ...prevState, data: [], loading: false }));
      showInternalError();
    }
  };
 
  const prepareJsonHeaderAndValueFormats = aggregatePartNames => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      partName => ({
        label: partName + ' Status',
        value: row =>
          row.materialGroupClusters.filter(group => group.name === partName)[0]
            ?.materials[0]?.aggregateStatus || 'unavailable',
      }),
    );
    

    const vcHeaderAndValueFormats = [
      {
        label: 'ASN',
        value: row => row.asn,
        
      },
      {
        label: `${parentName} Number`,
        value: row => row.vc,
      },
      {
        label: `${parentName} Description`,
        value: row => row.vcDescription,
      },
      {
        label: `${parentName} Status`,
        value: row => row.vcStatus,
      },
      {
        label: 'Part Availability',
        value: row => '="' + row.materialNumber + '"',
      },
      {
        label: 'Part Description',
        value: row => row.materialDescription,
      },
      {
        label: 'Aggregate Status',
        value: row => row.materialStatus,
      },
      {
        label: 'Updated At',
        value: row => {
          const formattedDate = moment(row.materialUpdatedAt).format('DD-MMMM-YYYY'); 
          const formattedTime = moment(row.materialUpdatedAt).format('hh:mm A');             
          return `${formattedTime} ${formattedDate}`;     
        }, 
      },
    ].filter(c => c);

    return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
  };

  const generateAndDownloadLineoutCsvReport = filteredData => {
    try {
      if (!rowsData.loading) {
        const dateValue = moment().format(DATE_FORMAT.ISO);
        
        const jsonData = filteredData;

        const aggregatePartNames = [];

        const { parseAsync } = require('json2csv');
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames);
        const opts = { fields };
        parseAsync(jsonData, opts)
          .then(csv => {
            const exportedFilename =
              'AsnAvailabilityReport_' +
              plant.label +
              '_' +
              line.label +
              '_' +
              dateValue +
              '.csv';
            downloadCsv(csv, exportedFilename);
          })
          .catch(error =>
            console.error(
              'Failed to download Asn Availability report, error:' +
                buildErrorMessage(error),
            ),
          );
      }
    } catch (error) {
      console.error(
        'Failed to download Asn Availability report, error:' + buildErrorMessage(error),
      );
    }
  };

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;

    setActiveAsnDate(null);
    loadAsnDates(
      plant.value,
      line.value,
      setAsnDates,
      setActiveAsnDate,
      null,
      latestPlantValueRef,
      latestLineValueRef,
    ).catch(err => console.error(buildErrorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value]);

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    rowsData.loading,
    hasActiveEdits,
    loadAggregateAsnForDate,
    materialGroupCluster,
    activeAsnDate,
    plant.value,
    line.value,
  );

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some(row => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  return (
    <>
      <AggregateToolbar
        activeDate={activeAsnDate}
        dates={asnDates}
        handleDateSelect={date => setActiveAsnDate(date)}
        selectedOptionalColNames={selectedOptionalColNames}
        applyColumnsSelection={selectedColName =>
          setSelectedOptionalColNames(selectedColName)
        }
        lastRefreshedDate={lastRefreshedDate}
        contextualButtonProps={{
          className: clsx(
            moment().subtract(1, 'days').isSame(activeAsnDate, 'day')
              ? styles.greenRoundButton
              : styles.greyRoundButton,
          ),
          text: 'Previous',
          label: 'Prev',
          handleClick: () => setActiveAsnDate(moment().subtract(1, 'days')),
        }}
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
      />
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: '100%' }}>
          <Table
            columns={[
              ...aggregateColumns,
              ...vcColumns,
              ...optionalColumnsWithShift.map(c => ({
                ...c,
                enableSearch: true,
                enableFilter: true,
              })),
              ...actionColumns,
            ]}
            defaultColumns={[
              ...vcColumns,
              ...aggregateColumns,
              ...actionColumns,
            ]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.data}
            primaryAction={{
              name: 'Available',
              actionFn: markAvailable,
              authOperation: USER_OPERATIONS.UPDATE_AGGREGATE_DISPATCH,
              shouldEnable: selectedRows =>
                selectedRows.length &&
                selectedRows.every(
                  row =>
                    row.materialStatus ===
                    statusStringMap[AGGREGATE_STATUS.PLANNED],
                ),
            }}
            secondaryActions={[
              {
                name: 'DOWNLOAD CSV',
                getFilteredData: true,
                authOperation: USER_OPERATIONS.GET_CHASSIS_PLAN_ASN,
                shouldEnable: () => rowsData.data.length > 0,
                actionFn: filteredData =>
                  generateAndDownloadLineoutCsvReport(filteredData),
              },
            ]}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={() => {}}
          />
          {rowsData.loading && (
            <div>
              <LinearProgress />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

AvailabilityTab.propTypes = {
  aggregate: PropTypes.string.isRequired,
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  AvailabilityTab,
  false,
  RESOURCE_TYPE.AGGREGATE,
);


